import { Check, Leaf } from "lucide-react";

const SERVICES = [
  {
    n: "01",
    title: "Human Resources Consulting",
    tagline:
      "Helping organizations develop their greatest asset — their people.",
    accent: "gold",
    intro:
      "Our Human Resources Consulting services are designed to help organizations build strong, compliant, and high-performing workforces. With more than 20 years of executive leadership and federal experience, we provide strategic guidance that aligns people, processes, and organizational goals.",
    audience:
      "We partner with government agencies, nonprofit organizations, and small businesses to strengthen organizational effectiveness, improve employee engagement, and develop sustainable workforce solutions.",
    closing:
      "Our collaborative approach equips leaders with practical strategies to foster accountability, increase productivity, and build resilient organizations prepared for long-term success.",
    expertise: [
      "Human Capital Strategy and Workforce Planning",
      "Organizational Development and Change Management",
      "Talent Acquisition and Workforce Optimization",
      "Employee Relations and Performance Management",
      "Leadership Coaching and Executive Development",
      "Policy and Procedure Development",
      "Compliance with Federal Employment Regulations",
      "Position Classification and Workforce Restructuring",
      "Strategic Planning and Operational Excellence",
      "Training, Mentoring, and Professional Development",
      "Performance Improvement Initiatives",
      "Cross-Functional Team Leadership and Stakeholder Engagement",
    ],
  },
  {
    n: "02",
    title: "Strategic Consulting & Leadership Development",
    tagline:
      "Equipping leaders to navigate complexity, cultivate excellence, and achieve sustainable growth.",
    accent: "green",
    intro:
      "Drawing upon extensive executive leadership and federal contracting experience, we equip individuals, businesses, nonprofit organizations, government agencies, and ministry leaders with the knowledge, strategies, and resources needed to navigate complex challenges, lead with purpose, and achieve meaningful results.",
    audience:
      "Through consulting, training, mentorship, speaking engagements, and transformational programs, we empower leaders to operate with confidence, wisdom, and resilience while fulfilling their purpose.",
    closing:
      "We believe transformation begins within and is reflected through every area of leadership, service, and stewardship — building strategic partnerships and creating a legacy of positive change for generations to come.",
    expertise: [
      "Strategic Consulting & Executive Advisory",
      "Procurement & Acquisition Expertise",
      "Leadership Development & Coaching",
      "Organizational Transformation",
      "Training & Mentorship Programs",
      "Speaking Engagements & Keynotes",
      "Transformational Leadership Programs",
      "Purpose-Driven Empowerment for Ministry Leaders",
    ],
  },
];

function ServiceBlock({ svc, index }) {
  const accent = svc.accent === "green" ? "#6B6B6B" : "#C9A66B";
  const reverse = index % 2 === 1;
  return (
    <article
      data-testid={`service-${svc.n}`}
      className={`grid grid-cols-12 gap-8 md:gap-10 lg:gap-16 py-16 md:py-24 lg:py-32 ${
        index !== 0 ? "border-t border-[#2A2A2A]" : ""
      }`}
    >
      {/* Left column — heading */}
      <div
        className={`col-span-12 lg:col-span-5 reveal ${
          reverse ? "lg:order-2" : ""
        }`}
      >
        <div className="lg:sticky lg:top-28">
          <div className="flex items-baseline gap-4 mb-6">
            <span
              className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-none"
              style={{ color: accent }}
            >
              {svc.n}
            </span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#A0A0A0]">
              Service
            </span>
          </div>
          <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#EDEDED] font-light leading-[1.1] tracking-tight">
            {svc.title}
          </h3>
          <p className="mt-6 font-serif italic text-lg md:text-xl text-[#A0A0A0] leading-relaxed">
            {svc.tagline}
          </p>
          <div
            className="mt-8 w-16 h-px"
            style={{ backgroundColor: accent }}
          />
        </div>
      </div>

      {/* Right column — copy + list */}
      <div
        className={`col-span-12 lg:col-span-7 reveal ${
          reverse ? "lg:order-1" : ""
        }`}
      >
        <p className="text-base md:text-lg leading-relaxed text-[#A0A0A0] font-light">
          {svc.intro}
        </p>

        <div className="mt-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A0A0A0] mb-6 flex items-center gap-2">
            <Leaf size={12} style={{ color: accent }} />
            Areas of Expertise
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {svc.expertise.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 text-[15px] text-[#EDEDED] font-light leading-snug"
                data-testid={`service-${svc.n}-item`}
              >
                <Check
                  size={16}
                  className="mt-1 flex-shrink-0"
                  style={{ color: accent }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 pt-8 border-t border-[#2A2A2A] space-y-4">
          <p className="text-[15px] leading-relaxed text-[#A0A0A0] font-light">
            {svc.audience}
          </p>
          <p className="text-[15px] leading-relaxed text-[#A0A0A0] font-light">
            {svc.closing}
          </p>
        </div>
      </div>
    </article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-[#0A0A0A] border-t border-[#2A2A2A]"
    >
      {/* Intro band */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-24 md:pt-32 lg:pt-40 pb-4">
        <div className="grid grid-cols-12 gap-8 md:gap-12">
          <div className="col-span-12 md:col-span-4 reveal">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#C9A66B] mb-4 flex items-center gap-2">
              <Leaf size={14} className="text-[#6B6B6B]" />
              What We Offer
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#EDEDED]">
              Two disciplines. <br />
              One standard of <em className="text-[#6B6B6B]">excellence</em>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6 reveal">
            <p className="text-base md:text-lg text-[#A0A0A0] font-light leading-relaxed">
              Becoming H.E.R. LLC is a transformational, purpose-driven
              consulting organization committed to developing people,
              strengthening organizations, and advancing excellence through
              integrity, innovation, and service. Rooted in the principles of{" "}
              <span className="text-[#EDEDED]">Honor</span>,{" "}
              <span className="text-[#EDEDED]">Excellence</span>, and{" "}
              <span className="text-[#EDEDED]">Resilience</span> — our services
              are designed to create meaningful and lasting impact.
            </p>
          </div>
        </div>
      </div>

      {/* Service blocks */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {SERVICES.map((svc, i) => (
          <ServiceBlock key={svc.n} svc={svc} index={i} />
        ))}
      </div>

      {/* Closing band */}
      <div className="bg-[#000000] text-[#EDEDED] grain relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28 text-center reveal relative">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#C9A66B] mb-6">
            §  Our Commitment
          </div>
          <p className="font-serif text-2xl md:text-3xl lg:text-4xl italic font-light leading-[1.3] text-[#EDEDED]">
            “Our commitment to <span className="text-[#C9A66B]">honor</span>,{" "}
            <span className="text-[#C9A66B]">excellence</span>, and{" "}
            <span className="text-[#C9A66B]">resilience</span> enables us to
            build strategic partnerships, develop impactful leaders, and
            deliver innovative solutions that strengthen organizations, inspire
            communities, and create a legacy of positive change for
            generations to come.”
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <span className="w-10 h-px bg-[#6B6B6B]" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#EDEDED]">
              Becoming H.E.R. LLC
            </span>
            <span className="w-10 h-px bg-[#6B6B6B]" />
          </div>
        </div>
      </div>
    </section>
  );
}
